import type { ReactNode } from "react";
import clsx from "clsx";
import type { MahnungStatus } from "@/lib/mahnwesen";

const SCHRITTE: Array<{ key: "entwurf" | "erstellt" | "versendet"; titel: string; text: string }> = [
  { key: "entwurf", titel: "Entwurf prüfen", text: "Empfänger, Rechnungen und Texte kontrollieren" },
  { key: "erstellt", titel: "Finalisieren", text: "Nummer vergeben, PDF wird unveränderlich archiviert" },
  { key: "versendet", titel: "Versenden", text: "Per E-Mail aus der App oder per Post" },
];

/** Anzahl erledigter Schritte je Status; der nächste Schritt ist der aktive. */
function erledigteSchritte(status: MahnungStatus | string): number {
  switch (status) {
    case "entwurf":
      return 0;
    case "erstellt":
    case "fehlgeschlagen":
      // Entwurf geprüft + finalisiert → jetzt versenden.
      return 2;
    case "versendet":
      return 3;
    default:
      return 0;
  }
}

/**
 * Drei-Schritte-Anzeige über dem Mahnungs-Editor: Entwurf → Finalisiert → Versendet.
 * Alles vor dem aktiven Schritt gilt als erledigt.
 */
export function MahnungStatusStepper({
  status,
  className,
}: {
  status: MahnungStatus | string;
  className?: string;
}) {
  const storniert = status === "storniert";
  const erledigtBis = storniert ? -1 : erledigteSchritte(status);
  return (
    <ol className={clsx("grid gap-2 sm:grid-cols-3", className)} aria-label="Ablauf">
      {SCHRITTE.map((schritt, idx) => {
        const erledigt = idx < erledigtBis;
        const istAktiv = !storniert && idx === erledigtBis && erledigtBis < SCHRITTE.length;
        const marker: ReactNode = erledigt ? "✓" : idx + 1;
        return (
          <li
            key={schritt.key}
            className={clsx(
              "flex items-start gap-3 rounded-xl border px-3 py-2.5",
              erledigt
                ? "border-status-green/40 bg-status-green/10"
                : istAktiv
                  ? "border-accent/50 bg-accent/10 ring-1 ring-accent/30"
                  : "border-separator/70 bg-card",
              storniert && "opacity-60",
            )}
            aria-current={istAktiv ? "step" : undefined}
          >
            <span
              className={clsx(
                "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold",
                erledigt
                  ? "bg-status-green text-label-on-accent"
                  : istAktiv
                    ? "bg-accent text-label-on-accent"
                    : "bg-fill-tertiary text-label-secondary",
              )}
            >
              {marker}
            </span>
            <span className="min-w-0">
              <span className={clsx("block text-sm font-semibold", istAktiv ? "text-label" : "text-label-secondary")}>
                {schritt.titel}
                {istAktiv ? <span className="ml-2 text-xs font-medium text-accent">Jetzt</span> : null}
              </span>
              <span className="block text-xs text-label-tertiary">{schritt.text}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
